import { Stack } from '@mui/material';
import { InvoicesReportingDayPastDue } from '@schooly/api';
import { FC, PropsWithChildren, useCallback, useState } from 'react';

import { ExpandedSelect } from '../ExpandedSelect';
import { ExpandedSelectFooterButtons } from '../ExpandedSelectContentContainer';
import { toggleMultipleValueArrayProperty } from '../utils';
import { DAY_PAST_DUE_TYPES } from './DayPastDueSelectMultiple';
import { DayPastDueTagSelect, DayPastDueTagSelectProps } from './DayPastDueTagSelect';

type DayPastDueExpandedSelectProps = PropsWithChildren<{
  selectedTypes: InvoicesReportingDayPastDue[];
  onApply: (v: InvoicesReportingDayPastDue[]) => void;
  onClear: () => void;
  onClose: () => void;
}>;
export const DayPastDueExpandedSelect: FC<DayPastDueExpandedSelectProps> = ({
  selectedTypes: initialSelectedTypes,
  onApply,
  onClose,
  onClear,
}) => {
  const [selectedTypes, setSelectedTypes] =
    useState<InvoicesReportingDayPastDue[]>(initialSelectedTypes);

  const toggleTypes = useCallback((v: InvoicesReportingDayPastDue[]) => {
    setSelectedTypes((types) => toggleMultipleValueArrayProperty(types, v));
  }, []);

  const handleApply = useCallback(() => {
    onApply(selectedTypes);
    onClose();
  }, [selectedTypes, onApply, onClose]);

  const renderContent = useCallback(() => {
    return (
      <Stack m={1} gap={2}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {renderDayPastDueTags({
            types: DAY_PAST_DUE_TYPES,
            tagProps: (type) => ({
              variant: selectedTypes.includes(type) ? 'filled' : undefined,
            }),
            onClick: (type) => toggleTypes([type]),
          })}
        </Stack>
        <ExpandedSelectFooterButtons onApply={handleApply} onClear={onClear} />
      </Stack>
    );
  }, [handleApply, onClear, toggleTypes, selectedTypes]);

  return (
    <ExpandedSelect
      hasSelectedValue={selectedTypes.length > 0}
      renderContent={renderContent}
      onClose={onClose}
      onClear={onClear}
    >
      {renderDayPastDueTags({
        types: selectedTypes,
        tagProps: { sx: { maxWidth: 200 }, size: 'small' },
        onDelete: (type) => toggleTypes([type]),
      })}
    </ExpandedSelect>
  );
};

type RenderDayPastDueTagsParams = {
  types: InvoicesReportingDayPastDue[];
  onDelete?: (v: InvoicesReportingDayPastDue) => void;
  onClick?: (v: InvoicesReportingDayPastDue) => void;
  tagProps?:
    | Omit<DayPastDueTagSelectProps, 'type'>
    | ((v: InvoicesReportingDayPastDue) => Omit<DayPastDueTagSelectProps, 'type'>);
};

export const renderDayPastDueTags = ({
  types,
  onDelete,
  onClick,
  tagProps,
}: RenderDayPastDueTagsParams) =>
  types.map((v) => (
    <DayPastDueTagSelect
      key={v}
      type={v}
      onClick={onClick ? () => onClick(v) : undefined}
      onDelete={onDelete ? () => onDelete(v) : undefined}
      {...(typeof tagProps === 'function' ? tagProps(v) : tagProps)}
    />
  ));
